import React, { useState, useMemo } from 'react';
import { ArrowLeft, UserMinus, User, CalendarClock, Mail, Cloud, HardDrive, Send, Loader2, AlertTriangle, X, AtSign, Laptop } from 'lucide-react';
import { EMAIL_RE, inputCls, Field, CheckGroup, ReferentCard, SuccessView } from './PcChangeRequestForm';
import { slug, Light, StepCard, LICENSES } from './ReassignWizard';

const BLOCK = ["Alle 18:00 dell'ultimo giorno", 'Subito, appena ricevuto il ticket'];
const MAIL = [{ v: 'Risponditore automatico', d: 'La casella resta attiva per un periodo con risposta automatica' }, { v: 'Chiudere tutto', d: 'Casella disattivata, posta conservata in archivio' }];
const DATA = [{ v: 'Trasferire a un collega', d: 'OneDrive e file passano a un collega' }, { v: 'Archiviare', d: 'Conservati senza assegnarli a nessuno' }, { v: 'Eliminare', d: 'Nessun dato da conservare' }];
const CLEAN = ['Sì, formattare', 'No, mantieni dati'];
const Q = ({ children }) => <p className="text-xs font-medium text-slate-400 mb-2">{children}</p>;
const Lights = ({ options, value, onChange, prefix, cols = 'sm:grid-cols-2', recommended }) => (
    <div className={`grid grid-cols-1 ${cols} gap-2`}>
        {options.map(o => { const v = o.v || o; return <Light key={v} label={v} desc={o.d} active={value === v} recommended={recommended === v} onClick={() => onChange(v)} testid={`${prefix}-${slug(v)}`} />; })}
    </div>
);
const who = (f) => f.leaving_name.trim() || 'il dipendente';
const fmt = (d) => (d ? new Date(d).toLocaleDateString('it-IT') : '');

const StepWho = ({ f, set, needRequester }) => (
    <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nome e cognome *" icon={User}><input value={f.leaving_name} onChange={set('leaving_name')} placeholder="Es. Mario Rossi" className={inputCls} data-testid="ex-name" /></Field>
            <Field label="Azienda *"><input value={f.company} onChange={set('company')} className={inputCls} data-testid="ex-company" /></Field>
            <Field label="Su quale PC lavorava?" icon={Laptop}><input value={f.leaving_pc} onChange={set('leaving_pc')} placeholder="Es. PC-AMMIN-04" className={inputCls} data-testid="ex-pc" /></Field>
        </div>
        <div><Q>Aveva una email aziendale? *</Q><Lights options={['Sì', 'No']} value={f.had_email} onChange={set('had_email')} prefix="ex-had-email" cols="grid-cols-2" /></div>
        {f.had_email === 'Sì' && <Field label={`Email di ${who(f)} *`} icon={AtSign}><input type="email" value={f.leaving_email} onChange={set('leaving_email')} placeholder="mario.rossi@azienda.it" className={inputCls} data-testid="ex-email" /></Field>}
        {needRequester && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-800">
                <Field label="Il tuo nome e cognome *"><input value={f.referent_name} onChange={set('referent_name')} className={inputCls} data-testid="ex-req-name" /></Field>
                <Field label="La tua email *"><input type="email" value={f.referent_email} onChange={set('referent_email')} className={inputCls} data-testid="ex-req-email" /></Field>
            </div>
        )}
    </div>
);

const StepWhen = ({ f, set }) => (
    <div className="space-y-3">
        <Field label="Ultimo giorno di lavoro *" icon={CalendarClock}><input type="date" lang="it" value={f.last_day} onChange={set('last_day')} className={inputCls} data-testid="ex-last-day" /></Field>
        <div><Q>Quando bloccare gli accessi di {who(f)}? *</Q><Lights options={BLOCK} value={f.block_when} onChange={set('block_when')} prefix="ex-block" recommended={BLOCK[0]} /></div>
    </div>
);

const StepMail = ({ f, set }) => (
    <div className="space-y-3">
        <Q>Cosa succede alla casella {f.leaving_email}? *</Q>
        <Lights options={MAIL} value={f.email_future} onChange={set('email_future')} prefix="ex-mail" />
        {f.email_future === MAIL[0].v && <input value={f.autoreply_text} onChange={set('autoreply_text')} placeholder="Chi contattare al suo posto (es. scrivere a ufficio@azienda.it)" className={inputCls} data-testid="ex-autoreply" />}
        {f.email_future === MAIL[1].v && <input value={f.archive_access_to} onChange={set('archive_access_to')} placeholder="Chi deve vedere l'archivio posta? (vuoto se nessuno)" className={inputCls} data-testid="ex-archive-to" />}
    </div>
);

const StepData = ({ f, set }) => (
    <div className="space-y-3">
        <Q>Cosa facciamo con OneDrive e i file di {who(f)}? *</Q>
        <Lights options={DATA} value={f.data_action} onChange={set('data_action')} prefix="ex-data" cols="sm:grid-cols-3" />
        {f.data_action === DATA[0].v && <Field label="Nome e cognome del collega *" icon={User}><input value={f.data_transfer_to} onChange={set('data_transfer_to')} placeholder="Es. Anna Neri" className={inputCls} data-testid="ex-data-to" /></Field>}
    </div>
);

const StepDevice = ({ f, set }) => (
    <div className="space-y-4">
        <div><Q>Ripulire il suo dispositivo? *</Q><Lights options={CLEAN} value={f.clean_device} onChange={set('clean_device')} prefix="ex-clean" cols="grid-cols-2" /></div>
        <div><Q>Recuperare le licenze installate sul PC? *</Q><Lights options={['Sì', 'No']} value={f.license_recovery} onChange={set('license_recovery')} prefix="ex-licrec" cols="grid-cols-2" /></div>
        {f.license_recovery === 'Sì' && (
            <div className="space-y-2" data-testid="ex-licrec-block">
                <CheckGroup options={LICENSES} value={f.license_recovery_items} onChange={set('license_recovery_items')} prefix="ex-licrec-item" />
                <input value={f.license_recovery_other} onChange={set('license_recovery_other')} placeholder="Altri software / numero di serie o account licenza" className={inputCls} data-testid="ex-licrec-other" />
            </div>
        )}
        <div><Q>Aveva accesso VPN da disattivare? *</Q><Lights options={['Sì', 'No']} value={f.had_vpn} onChange={set('had_vpn')} prefix="ex-vpn" cols="grid-cols-2" /></div>
    </div>
);

const EMPTY = {
    company: '', leaving_name: '', leaving_pc: '', had_email: '', leaving_email: '', last_day: '', block_when: '',
    email_future: '', autoreply_text: '', archive_access_to: '', data_action: '', data_transfer_to: '',
    clean_device: '', license_recovery: '', license_recovery_items: [], license_recovery_other: '', had_vpn: '', notes: '',
    referent_name: '', referent_email: '', confirm: false,
};

const buildSteps = (f, needRequester) => {
    const g = f.leaving_name.trim() || 'Dipendente in uscita';
    const reqOk = !needRequester || (f.referent_name.trim() && EMAIL_RE.test(f.referent_email.trim()));
    return [
        { id: 'who', group: g, icon: User, title: 'Chi lascia l\'azienda, PC ed email', ok: f.leaving_name.trim() && f.company.trim() && f.had_email && (f.had_email === 'No' || EMAIL_RE.test(f.leaving_email.trim())) && reqOk, summary: `${f.leaving_name} · ${f.company}${f.leaving_pc ? ` · ${f.leaving_pc}` : ''}${f.had_email === 'Sì' ? ` · ${f.leaving_email}` : ' · nessuna email'}` },
        { id: 'when', group: g, icon: CalendarClock, title: 'Ultimo giorno e blocco accessi', ok: !!f.last_day && !!f.block_when, summary: `Ultimo giorno ${fmt(f.last_day)} · ${f.block_when}` },
        ...(f.had_email === 'Sì' ? [{ id: 'mail', group: 'Email', icon: Mail, title: 'Cosa succede alla casella', ok: !!f.email_future, summary: f.email_future }] : []),
        { id: 'data', group: 'Dati', icon: Cloud, title: 'OneDrive e file', ok: !!f.data_action && (f.data_action !== DATA[0].v || !!f.data_transfer_to.trim()), summary: f.data_action === DATA[0].v ? `Trasferire a ${f.data_transfer_to}` : f.data_action },
        { id: 'dev', group: 'Dispositivo', icon: HardDrive, title: 'Dispositivo, licenze e VPN', ok: !!f.clean_device && !!f.license_recovery && !!f.had_vpn, summary: `${f.clean_device} · licenze ${f.license_recovery} · VPN ${f.had_vpn}` },
    ];
};

export const ExitWizard = ({ apiBase, logo, prefill = {}, onClose, onBack }) => {
    const [f, setF] = useState({ ...EMPTY, company: prefill.company || '' });
    const [done, setDone] = useState([]);
    const [open, setOpen] = useState('who');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [ticket, setTicket] = useState(null);
    const referent = onClose ? { name: prefill.userName || '', email: prefill.email || '', phone: prefill.phone || '', company: prefill.company || '' } : null;
    const set = (k) => (v) => setF(s => ({ ...s, [k]: v && v.target ? v.target.value : v }));
    const steps = useMemo(() => buildSteps(f, !referent), [f, referent]);
    const allDone = steps.every(s => done.includes(s.id) && s.ok);
    const next = (i) => {
        const nd = done.includes(steps[i].id) ? done : [...done, steps[i].id];
        setDone(nd);
        const nxt = steps.find(s => !nd.includes(s.id) || !s.ok);
        setOpen(nxt ? nxt.id : null);
    };
    const stateOf = (s, i) => (open === s.id ? 'open' : done.includes(s.id) && s.ok ? 'done' : (i === 0 || steps.slice(0, i).every(p => done.includes(p.id) && p.ok)) ? 'ready' : 'locked');
    const render = (id) => ({
        who: <StepWho f={f} set={set} needRequester={!referent} />, when: <StepWhen f={f} set={set} />, mail: <StepMail f={f} set={set} />,
        data: <StepData f={f} set={set} />, dev: <StepDevice f={f} set={set} />,
    })[id];

    const submit = async () => {
        setError('');
        if (!f.confirm) return setError(`Conferma che le informazioni su ${who(f)} sono corrette.`);
        setLoading(true);
        try {
            const r = await fetch(`${apiBase}/api/v1/public/employee-exit`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...f, leaving_email: f.had_email === 'Sì' ? f.leaving_email.trim() : '', email_future: f.had_email === 'Sì' ? f.email_future : '',
                    source: onClose ? 'spark-app' : 'link',
                    ...(referent ? { referent_name: referent.name, referent_email: referent.email, referent_phone: referent.phone, referent_company: referent.company } : {}),
                }),
            });
            const d = await r.json().catch(() => ({}));
            if (r.ok && d.success) setTicket(d.ticket_key); else setError(typeof d.detail === 'string' ? d.detail : 'Invio non riuscito.');
        } catch (e) { setError('Errore di connessione. Riprova.'); }
        setLoading(false);
    };

    const progress = Math.round((steps.filter(s => done.includes(s.id) && s.ok).length / steps.length) * 100);
    const body = (
        <div className="w-full max-w-2xl mx-auto py-8 px-4" data-testid="ex-wizard">
            {onBack && !ticket && <button onClick={onBack} className="mb-4 text-sm text-slate-400 hover:text-white flex items-center gap-1.5" data-testid="pc-form-back"><ArrowLeft className="w-4 h-4" /> Gestione Operativa</button>}
            <div className="text-center mb-5">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-rose-600/20 border border-rose-500/40 mb-3"><UserMinus className="w-7 h-7 text-rose-300" /></div>
                <h1 className="text-2xl font-bold" data-testid="pc-form-title">Uscita dipendente</h1>
                <p className="text-sm text-slate-400 mt-1">Sicurezza dei dati e continuità del lavoro</p>
            </div>
            {ticket ? <SuccessView ticketKey={ticket} onClose={onClose} /> : (
                <div className="space-y-3">
                    {referent && <ReferentCard r={referent} />}
                    <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden" data-testid="ex-progress"><div className="h-full bg-gradient-to-r from-rose-500 to-emerald-400 transition-[width] duration-500" style={{ width: `${progress}%` }} /></div>
                    {steps.map((s, i) => (
                        <StepCard key={s.id} idx={i} step={s} state={stateOf(s, i)} onOpen={() => setOpen(open === s.id ? null : s.id)} onNext={() => next(i)} canNext={!!s.ok}>
                            {render(s.id)}
                        </StepCard>
                    ))}
                    {allDone && (
                        <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-4 space-y-3" data-testid="ex-final">
                            <textarea rows={2} value={f.notes} onChange={set('notes')} placeholder="Note aggiuntive (facoltative)" className={`${inputCls} resize-none`} data-testid="ex-notes" />
                            <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer">
                                <input type="checkbox" checked={f.confirm} onChange={e => set('confirm')(e.target.checked)} className="accent-emerald-500 w-4 h-4 mt-0.5" data-testid="ex-confirm" />
                                <span>Confermo che le informazioni su {who(f)} sono corrette e complete *</span>
                            </label>
                            {error && <p className="text-sm text-red-400 flex items-center gap-1.5" data-testid="ex-error"><AlertTriangle className="w-4 h-4" /> {error}</p>}
                            <button onClick={submit} disabled={loading} className="w-full py-3 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-60" data-testid="ex-submit">
                                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />} Invia richiesta di uscita
                            </button>
                        </div>
                    )}
                </div>
            )}
            <div className="text-center mt-6 pt-5 border-t border-white/10">
                {logo && <img src={logo} alt="86BIT" className="h-8 mx-auto mb-2 opacity-90" />}
                <p className="text-slate-500 text-[10px]">86BIT srl Unipersonale · P.Iva 04353030168</p>
            </div>
        </div>
    );
    if (onClose) {
        return (
            <div className="fixed inset-0 z-[80] overflow-y-auto bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white" data-testid="pc-form-overlay">
                <button onClick={onClose} title="Chiudi" className="fixed top-3 right-3 z-[81] p-2 rounded-full bg-slate-800/90 hover:bg-slate-700 border border-slate-700" data-testid="pc-form-close"><X className="w-5 h-5" /></button>
                {body}
            </div>
        );
    }
    return <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white" data-testid="pc-form-page">{body}</div>;
};

export default ExitWizard;
